import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import CustomEditor from './ui/custom-text-editor';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Paperclip, Send, Tag, MessageSquare, MoreVertical, Trash2, Reply, FileText, AlertTriangle, CheckCircle, Clock, Loader2 } from 'lucide-react';
import { Correspondence, User } from '../types';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { CORRESPONDENCE_TEMPLATES } from '../constants/correspondenceTemplates';
import { formatDistanceToNow } from 'date-fns';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogTrigger,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogClose,
    DialogDescription,
} from "./ui/dialog";
import { Checkbox } from './ui/checkbox';

const escapeHtml = (text: string) => {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
};

const isSectionHeader = (line: string) => {
    const t = line.trim();
    if (/^\[.*\]$/.test(t)) return false;
    if (t.startsWith('Dear ')) return false;
    if (t.startsWith('Yours sincerely')) return false;
    if (t.startsWith('Yours faithfully')) return false;
    if (t.startsWith('Regards')) return false;
    if (t === t.toUpperCase() && t.length > 5 && !t.includes('[') && !t.includes(':')) return true;
    if (!t.includes(':') && t.length >= 10 && t.length <= 60 && !/[.?!]$/.test(t)) return true;
    if (t.endsWith(':') && t.length >= 30 && t.length <= 45) return true;
    return false;
};

const isDearLine = (line: string) => line.trim().startsWith('Dear ');

const isSignatureLine = (line: string) => {
    const t = line.trim();
    return t.startsWith('Employee Signature:') || t.startsWith('Manager Signature:');
};

const plainTextToHtml = (text: string) => {
    const lines = text.split('\n');
    const parts: string[] = [];
    let currentParaLines: string[] = [];
    let currentBulletLines: string[] = [];

    const flushPara = () => {
        if (currentParaLines.length > 0) {
            const joined = currentParaLines.map(escapeHtml).join('<br>');
            parts.push(`<p>${joined}</p>`);
            currentParaLines = [];
        }
    };

    const flushBullets = () => {
        if (currentBulletLines.length > 0) {
            const items = currentBulletLines.map((l) => {
                const clean = l.trim().replace(/^[•*]\s+/, '');
                return `<li>${escapeHtml(clean)}</li>`;
            });
            parts.push(`<ul>${items.join('')}</ul>`);
            currentBulletLines = [];
        }
    };

    const pushSpacer = () => {
        if (parts.length > 0 && parts[parts.length - 1] !== '<p><br></p>') {
            parts.push('<p><br></p>');
        }
    };

    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (line === '') {
            flushPara();
            flushBullets();
            continue;
        }

        const isBullet = line.startsWith('• ') || line.startsWith('* ');
        const isHeader = isSectionHeader(rawLine);
        const isDear = isDearLine(rawLine);

        if (isBullet) {
            if (currentBulletLines.length === 0) {
                flushPara();
                pushSpacer();
            }
            currentBulletLines.push(rawLine);
        } else if (isHeader || isDear || isSignatureLine(rawLine)) {
            flushPara();
            flushBullets();
            pushSpacer();
            parts.push(`<p>${escapeHtml(rawLine)}</p>`);
            parts.push('<p><br></p>');
        } else {
            flushBullets();
            currentParaLines.push(rawLine);
        }
    }

    flushPara();
    flushBullets();
    return parts.join('');
};

interface CorrespondenceTabProps {
    userId: string;
    managerId?: string | null;
    highlightNoteId?: string | null;
}

const CorrespondenceTab: React.FC<CorrespondenceTabProps> = ({ userId, managerId, highlightNoteId }) => {
    const { currentUser } = useAuth();
    const [threads, setThreads] = useState<Correspondence[]>([]);
    const [loading, setLoading] = useState(true);
    const threadRefs = useRef<Record<number, HTMLDivElement | null>>({});

    const [newMessage, setNewMessage] = useState('');
    const [selectedType, setSelectedType] = useState<string>('All');
    const [postType, setPostType] = useState<string>('General Feedback');
    const [tags, setTags] = useState<string[]>([]);
    const [currentTag, setCurrentTag] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [replyingTo, setReplyingTo] = useState<number | null>(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
    const [employeeName, setEmployeeName] = useState('');
    const [employeeJobRole, setEmployeeJobRole] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [requiresAcknowledgement, setRequiresAcknowledgement] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Acknowledgement is mandatory for warnings and performance development correspondence
    const isAcknowledgementMandatory = postType === 'Verbal Warning' || postType === 'Written Warning' || postType === 'Performance Development';

    const fetchCorrespondence = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/correspondence/user/${userId}?type=${selectedType}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setThreads(data.correspondence);
            }
        } catch (error) {
            console.error('Error fetching correspondence:', error);
            toast.error('Failed to load correspondence');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCorrespondence();
    }, [userId, selectedType]);

    useEffect(() => {
        const fetchEmployee = async () => {
            try {
                const data = await api.get<{ success: boolean; user: any }>(`/api/users/${userId}`, { skipErrorToast: true });
                if (data.success && data.user) {
                    setEmployeeName(data.user.name || '');
                    setEmployeeJobRole(data.user.jobRole || '');
                }
            } catch (error) {
                // Employee details are optional; placeholders will remain if unavailable
            }
        };
        fetchEmployee();
    }, [userId]);

    useEffect(() => {
        if (selectedType !== 'All') {
            setPostType(selectedType);
        }
    }, [selectedType]);

    useEffect(() => {
        // Changing the correspondence type resets the template selection and editor content
        setSelectedTemplate('');
        setNewMessage('');
    }, [postType]);

    useEffect(() => {
        // Mandatory acknowledgement for Verbal Warning, Written Warning and Performance Development
        if (postType === 'Verbal Warning' || postType === 'Written Warning' || postType === 'Performance Development') {
            setRequiresAcknowledgement(true);
        } else if (postType === 'Private File Notes') {
            setRequiresAcknowledgement(false);
        }
    }, [postType]);

    useEffect(() => {
        if (highlightNoteId && !loading && threads.length > 0) {
            const noteId = Number(highlightNoteId);
            const element = threadRefs.current[noteId];
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
                const timeout = setTimeout(() => {
                    element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
                }, 3000);
                return () => clearTimeout(timeout);
            }
        }
    }, [highlightNoteId, loading, threads]);



    const addCurrentTag = () => {
        if (currentTag.trim()) {
            if (!tags.includes(currentTag.trim())) {
                setTags([...tags, currentTag.trim()]);
            }
            setCurrentTag('');
        }
    };

    const handleAddTag = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addCurrentTag();
        }
    };

    const handleBlur = () => {
        addCurrentTag();
    };

    const removeTag = (tagToRemove: string) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (parentId?: number) => {
        const messageContent = parentId ? replyMessage : newMessage;
        if (!messageContent.trim() || isSubmitting) return;
        let data;
        setIsSubmitting(true);
        try {
            const token = localStorage.getItem('token');

            // 1. Create the correspondence
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/correspondence`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    type: postType,
                    message: messageContent,
                    tags: parentId ? [] : tags, // Only top-level messages have tags for now
                    employeeId: userId,
                    parentId,
                    requiresAcknowledgement: postType !== 'Private File Notes' ? requiresAcknowledgement : false
                })
            });

            data = await response.json();

            if (data.success) {
                const newCorrespondence = data.correspondence;

                // 2. Upload file if exists
                if (file && !parentId) { // Only allow file upload on top level for now or handle separately
                    const formData = new FormData();
                    formData.append('file', file);

                    await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/correspondence/${newCorrespondence.id}/upload`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                        body: formData
                    });
                }

                toast.success(data.message);

                setNewMessage('');
                setReplyMessage('');
                setTags([]);
                setFile(null);
                setReplyingTo(null);
                setRequiresAcknowledgement(false);
                fetchCorrespondence();
            } else {
                toast.error(data.message || 'Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Error sending message');
        } finally {
            setIsSubmitting(false);
        }
    };

    const openDeleteDialog = (id: number) => {
        setDeleteTargetId(id);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!deleteTargetId) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/correspondence/${deleteTargetId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                toast.success('Message deleted');
                fetchCorrespondence();
            } else {
                toast.error('Failed to delete message');
            }
        } catch (error) {
            console.error('Error deleting message:', error);
            toast.error('Error deleting message');
        } finally {
            setDeleteDialogOpen(false);
            setDeleteTargetId(null);
        }
    };

    const applyTemplate = (templateId: string) => {
        const template = CORRESPONDENCE_TEMPLATES.find((t) => t.id === templateId);
        if (!template) return;

        const now = new Date();
        const dateStr = now.toLocaleDateString('en-AU');
        const timeStr = now.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });

        const body = template.body
            .replace(/\[Employee Name\]/g, () => employeeName || '[Employee Name]')
            .replace(/\[Job Title\]/g, () => employeeJobRole || '[Job Title]')
            .replace(/\[Manager Name\]/g, () => currentUser?.name || '[Manager Name]')
            .replace(/\[Manager Title\]/g, () => currentUser?.jobRole || '[Manager Title]')
            .replace(/\[Date\]/g, () => dateStr)
            .replace(/\[Time\]/g, () => timeStr);

        const htmlBody = plainTextToHtml(body);
        setNewMessage(htmlBody);
        setSelectedTemplate(templateId);
    };

    const canViewPrivateNotes = (() => {
        if (!currentUser) return false;
        if (['Admin', 'Director', 'Account Owner'].includes(currentUser.userRole)) return true;
        if (managerId && String(currentUser.id) === String(managerId)) return true;
        return false;
    })();

    const canDeleteThread = (thread: Correspondence) => {
        if (thread.type === 'File Notes' || thread.type === 'Private File Notes') {
            if (!currentUser) return false;
            if (['Admin', 'Director', 'Account Owner'].includes(currentUser.userRole)) return true;
            if (managerId && String(currentUser.id) === String(managerId)) return true;
            return false;
        }
        return true;
    };

        const handleAcknowledge = async (correspondenceId: number) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/correspondence/${correspondenceId}/acknowledge`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Correspondence acknowledged');
                fetchCorrespondence();
            } else {
                toast.error(data.message || 'Failed to acknowledge');
            }
        } catch (error) {
            console.error('Error acknowledging correspondence:', error);
            toast.error('Error acknowledging correspondence');
        }
    };

    const isEmployee = currentUser && String(currentUser.id) === String(userId);
const visibleThreads = threads.filter(thread => {
        if (thread.type !== 'Private File Notes') return true;
        return canViewPrivateNotes;
    });

    return (
        <div className="space-y-6">
            <div className="flex flex-col space-y-2">
                <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                    <h2 className="text-2xl font-bold tracking-tight">Staff Correspondence</h2>
                    <div className="w-full sm:w-[200px]">
                        <Select value={selectedType} onValueChange={setSelectedType}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select Type" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="All">All Messages</SelectItem>
                                <SelectItem value="Verbal Warning">Verbal Warning</SelectItem>
                                <SelectItem value="Written Warning">Written Warning</SelectItem>
                                <SelectItem value="Coaching Advice">Coaching Advice</SelectItem>
                                <SelectItem value="General Feedback">General Feedback</SelectItem>
                                <SelectItem value="File Notes">File Notes</SelectItem>
                                <SelectItem value="Private File Notes">Private File Notes</SelectItem>
                                <SelectItem value="Performance Development">Performance Development</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <p className="text-muted-foreground">
                    Track communication, feedback, and HR related actions
                </p>
            </div>

            {/* New Message Input */}
            <Card>
                <CardContent className="pt-6 space-y-4">
                    <div className="flex gap-4">
                        <div className="flex-1 space-y-2">
                            <div className="w-full sm:w-[200px]">
                                <Select value={postType} onValueChange={setPostType}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Verbal Warning">Verbal Warning</SelectItem>
                                        <SelectItem value="Written Warning">Written Warning</SelectItem>
                                        <SelectItem value="Coaching Advice">Coaching Advice</SelectItem>
                                        <SelectItem value="General Feedback">General Feedback</SelectItem>
                                        <SelectItem value="File Notes">File Notes</SelectItem>
                                        <SelectItem value="Private File Notes">Private File Notes</SelectItem>
                                        <SelectItem value="Performance Development">Performance Development</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {postType !== 'Private File Notes' && (
                                <div className="flex items-center gap-2 py-2">
                                    <Checkbox
                                        id="requires-acknowledgement"
                                        checked={requiresAcknowledgement}
                                        onCheckedChange={(checked) => setRequiresAcknowledgement(checked === true)}
                                        disabled={isAcknowledgementMandatory}
                                    />
                                    <label
                                        htmlFor="requires-acknowledgement"
                                        className={`text-sm ${isAcknowledgementMandatory ? 'text-muted-foreground' : 'text-foreground'}`}
                                    >
                                        Require acknowledgement from employee
                                        {isAcknowledgementMandatory && (
                                            <span className="text-destructive ml-1">(Mandatory)</span>
                                        )}
                                    </label>
                                </div>
                            )}

                            {postType === 'Private File Notes' && (
                                <p className="text-xs text-red-500">
                                    You've selected private note. A private note is hidden from the employee and will only be visible to the manager and system administrators. The employee will not be notified.
                                </p>
                            )}

                            {(postType === 'Written Warning' || postType === 'File Notes' || postType === 'Performance Development') && (
                                <div className="w-full sm:w-[320px]">
                                    <Select value={selectedTemplate} onValueChange={applyTemplate}>
                                        <SelectTrigger>
                                            <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
                                            <SelectValue placeholder="Choose a template..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CORRESPONDENCE_TEMPLATES.filter((t) =>
                                                postType === 'File Notes'
                                                    ? t.category === 'file-note'
                                                    : postType === 'Written Warning'
                                                        ? t.category === 'written-warning'
                                                        : t.category === 'performance-development'
                                            ).map((t) => (
                                                <SelectItem key={t.id} value={t.id}>
                                                    {t.title}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Select a template to pre-fill. You can edit before sending.
                                    </p>
                                    {selectedTemplate && (
                                        <p className="text-xs text-destructive font-semibold mt-1 flex items-center gap-1">
                                            <AlertTriangle className="h-3 w-3" />
                                            Please replace all text in square brackets [like this] before sending.
                                        </p>
                                    )}
                                </div>
                            )}

                            <CustomEditor
                                value={newMessage}
                                onChange={setNewMessage}
                                placeholder="Type your message here..."
                                className="min-h-[200px]"
                            />

                            {/* Tags Input */}
                            <div className="flex flex-wrap gap-2 items-center border rounded-md p-2 bg-background">
                                <Tag className="h-4 w-4 text-muted-foreground" />
                                {tags.map((tag, index) => (
                                    <Badge key={index} variant="secondary" className="gap-1">
                                        {tag}
                                        <span className="cursor-pointer hover:text-destructive" onClick={() => removeTag(tag)}>×</span>
                                    </Badge>
                                ))}
                                <input
                                    type="text"
                                    placeholder="Add tags (press Enter)"
                                    value={currentTag}
                                    onChange={(e) => setCurrentTag(e.target.value)}
                                    onKeyDown={handleAddTag}
                                    onBlur={handleBlur}
                                    className="flex-1 bg-transparent outline-none text-sm"
                                />
                            </div>

                            {/* File Upload & Submit */}
                            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center pt-2 gap-4 sm:gap-0">
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="file"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        id="file-upload"
                                    />
                                    <label
                                        htmlFor="file-upload"
                                        className="cursor-pointer inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
                                    >
                                        <Paperclip className="h-4 w-4" />
                                        {file ? file.name : 'Attach file'}
                                    </label>
                                    {file && (
                                        <span className="text-xs text-muted-foreground cursor-pointer hover:text-destructive" onClick={() => setFile(null)}>
                                            (Remove)
                                        </span>
                                    )}
                                </div>
                                <Button onClick={() => handleSubmit()} className="w-full sm:w-auto" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="h-4 w-4 mr-2" />
                                            Post Message
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Threads List */}
            <div className="space-y-4">
                {loading ? (
                    <div>Loading...</div>
                ) : threads.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">No correspondence found.</div>
                ) : (
                    visibleThreads.map((thread) => (
                        <div
                            key={thread.id}
                            ref={(el) => { threadRefs.current[thread.id] = el; }}
                            className="rounded-xl transition-all duration-300"
                        >
                        <Card className="overflow-hidden">
                            <CardHeader className="bg-muted/30">
                                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-0">
                                    <div className="flex items-center gap-3 w-full sm:w-auto">
                                        <Avatar>
                                            <AvatarFallback>{thread.author.name.charAt(0)}</AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1 sm:flex-initial">
                                            <div className="font-semibold">{thread.author.name}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {formatDistanceToNow(new Date(thread.createdAt), { addSuffix: true })}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
                                        <Badge variant="outline">{thread.type}</Badge>
                                        {thread.requiresAcknowledgement && (
                                            <>
                                                {thread.acknowledged ? (
                                                    <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200 gap-1">
                                                        <CheckCircle className="h-3 w-3" />
                                                        Acknowledged
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200 gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        Pending
                                                    </Badge>
                                                )}
                                            </>
                                        )}
                                        {canDeleteThread(thread) && (
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => openDeleteDialog(thread.id)} className="text-destructive">
                                                        <Trash2 className="h-4 w-4 mr-2" />
                                                        Delete
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div
                                    className="text-sm ql-editor p-0 overflow-visible"
                                    dangerouslySetInnerHTML={{
                                        __html: /<[a-z][^>]*>/i.test(thread.message)
                                            ? thread.message
                                            : thread.message.replace(/\n/g, '<br>')
                                    }}
                                />

                                {thread.tags && thread.tags.length > 0 && (
                                    <div className="flex gap-2">
                                        {thread.tags.map((tag, i) => (
                                            <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                                        ))}
                                    </div>
                                )}

                                {thread.attachments && thread.attachments.length > 0 && (
                                    <div className="flex gap-2 flex-wrap mt-2">
                                        {thread.attachments.map((att) => {
                                            const isImage = att.mimetype.startsWith('image/');
                                            return (
                                                <Dialog key={att.id}>
                                                    <DialogTrigger asChild>
                                                        <div className="cursor-pointer hover:opacity-90 transition-opacity">
                                                            {isImage ? (
                                                                <img
                                                                    src={att.url}
                                                                    alt={att.filename}
                                                                    className="max-h-[200px] rounded-md object-cover border"
                                                                />
                                                            ) : (
                                                                <div className="flex items-center gap-2 text-sm text-primary hover:underline bg-muted/50 p-2 rounded-md border">
                                                                    <Paperclip className="h-4 w-4" />
                                                                    {att.filename}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </DialogTrigger>
                                                    <DialogContent className="max-w-4xl w-full p-4 overflow-hidden bg-white border shadow-lg rounded-lg">
                                                        <div className="flex flex-col gap-4">
                                                            <div className="flex items-center justify-between pr-8">
                                                                <h3 className="font-semibold truncate">{att.filename}</h3>
                                                            </div>
                                                            {isImage ? (
                                                                <div className="flex justify-center bg-muted/10 rounded-md overflow-hidden">
                                                                    <img
                                                                        src={att.url}
                                                                        alt={att.filename}
                                                                        className="max-w-full h-auto max-h-[80vh] object-contain"
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div className="bg-background p-6 rounded-md">
                                                                    <div className="flex flex-col items-center gap-4">
                                                                        <Paperclip className="h-12 w-12 text-muted-foreground" />
                                                                        <p className="text-lg font-medium">{att.filename}</p>
                                                                        <Button asChild>
                                                                            <a href={att.url} target="_blank" rel="noopener noreferrer">
                                                                                Download File
                                                                            </a>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </DialogContent>
                                                </Dialog>
                                            );
                                        })}
                                    </div>
                                )}

                                {thread.requiresAcknowledgement && !thread.acknowledged && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-sm text-amber-800">
                                            <Clock className="h-4 w-4" />
                                            <span>Awaiting acknowledgement from employee</span>
                                            {thread.remindersSent !== undefined && thread.remindersSent > 0 && (
                                                <span className="text-amber-600 text-xs">({thread.remindersSent} reminder{thread.remindersSent !== 1 ? 's' : ''} sent)</span>
                                            )}
                                        </div>
                                        {isEmployee && (
                                            <Button size="sm" onClick={() => handleAcknowledge(thread.id)}>
                                                <CheckCircle className="h-4 w-4 mr-2" />
                                                Acknowledge
                                            </Button>
                                        )}
                                    </div>
                                )}

                                {thread.requiresAcknowledgement && thread.acknowledged && (
                                    <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center gap-2 text-sm text-green-800">
                                        <CheckCircle className="h-4 w-4" />
                                        <span>Acknowledged by {thread.acknowledgedBy?.name || 'employee'}</span>
                                        {thread.acknowledgedAt && (
                                            <span className="text-green-600 text-xs">
                                                {formatDistanceToNow(new Date(thread.acknowledgedAt), { addSuffix: true })}
                                            </span>
                                        )}
                                    </div>
                                )}

                                <Separator />

                                {/* Replies */}
                                {thread.replies && thread.replies.length > 0 && (
                                    <div className="space-y-4 mt-4 border-l-2 border-muted">
                                        {thread.replies.map((reply) => (
                                            <div key={reply.id} className="bg-muted/50 p-4 rounded-lg space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Avatar className="h-6 w-6">
                                                        <AvatarFallback className="text-xs">{reply.author.name.charAt(0)}</AvatarFallback>
                                                    </Avatar>
                                                    <span className="font-semibold text-sm">{reply.author.name}</span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {formatDistanceToNow(new Date(reply.createdAt), { addSuffix: true })}
                                                    </span>
                                                </div>
                                                <div
                                                    className="text-sm pl-8 ql-editor p-0 overflow-visible"
                                                    dangerouslySetInnerHTML={{
                                                        __html: /<[a-z][^>]*>/i.test(reply.message)
                                                            ? reply.message
                                                            : reply.message.replace(/\n/g, '<br>')
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Reply Input */}
                                {replyingTo === thread.id ? (
                                    <div className="flex gap-2 items-start mt-2">
                                        <CustomEditor
                                            value={replyMessage}
                                            onChange={setReplyMessage}
                                            placeholder="Write a reply..."
                                            className="min-h-[120px]"
                                        />
                                        <div className="flex flex-col gap-2">
                                            <Button size="sm" onClick={() => handleSubmit(thread.id)} disabled={isSubmitting}>
                                                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reply'}
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => {
                                                setReplyingTo(null);
                                                setReplyMessage('');
                                            }}>Cancel</Button>
                                        </div>
                                    </div>
                                ) : (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground"
                                        onClick={() => setReplyingTo(thread.id)}
                                    >
                                        <Reply className="h-4 w-4 mr-2" />
                                        Reply
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                        </div>
                    ))
                )}
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent aria-describedby={undefined}>
                    <DialogHeader>
                        <DialogTitle>Delete Message</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this message? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button variant="destructive" onClick={handleDelete}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CorrespondenceTab;
